import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../shared/api';
import { useRealtimeEvents } from '../shared/useRealtimeEvents';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getContrastColor, COLOR_RED } from '../shared/colorUtils';
import './NotificationBell.css';

const NotificationBell = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const { data: unreadNotifications = [] } = useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: () => api.get('notifications/api/notifications/unread/'),
    staleTime: 60_000,
  });
  const unreadCount = unreadNotifications.length;

  const { data: notifications = [], isFetching: loading } = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: () => api.get('notifications/api/notifications/?ordering=-created_at&limit=10'),
    enabled: isOpen,
    select: (data) => (Array.isArray(data) ? data : data.results ?? []),
  });

  useRealtimeEvents((e) => {
    if (e.model !== 'notifications.notification') return;
    if (e.action === 'create') {
      queryClient.setQueryData(['notifications', 'unread'], (old = []) => [
        ...old,
        { id: Date.now(), is_read: false },
      ]);
    }
  });

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = () => setIsOpen((prev) => !prev);

  const markAsReadMutation = useMutation({
    mutationFn: (id) => api.post(`notifications/api/notifications/${id}/mark_as_read/`),
    onSuccess: (_, id) => {
      queryClient.setQueryData(['notifications', 'unread'], (old = []) =>
        old.filter((n) => n.id !== id)
      );
      queryClient.setQueryData(['notifications', 'list'], (old = []) =>
        old.map((n) => (n.id === id ? { ...n, is_read: true } : n))
      );
    },
    onError: (error) => console.error('Error marking notification as read:', error),
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: () => api.post('notifications/api/notifications/mark_all_read/'),
    onSuccess: () => {
      queryClient.setQueryData(['notifications', 'unread'], []);
      queryClient.setQueryData(['notifications', 'list'], (old = []) =>
        old.map((n) => ({ ...n, is_read: true }))
      );
    },
    onError: (error) => console.error('Error marking all as read:', error),
  });

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('notifications.justNow');
    if (diffMins < 60) return t('notifications.minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('notifications.hoursAgo', { count: diffHours });
    if (diffDays < 7) return t('notifications.daysAgo', { count: diffDays });

    return date.toLocaleDateString('cs-CZ');
  };

  return (
    <div className="nav-item dropdown d-flex me-3" ref={dropdownRef}>
      <a
        href="#"
        className="nav-link px-0"
        onClick={(e) => {
          e.preventDefault();
          handleToggle();
        }}
        tabIndex="-1"
        aria-label={t('notifications.title')}
      >
        <span style={{ position: 'relative', display: 'inline-flex' }}>
          <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
            <path d="M10 5a2 2 0 1 1 4 0a7 7 0 0 1 4 6v3a4 4 0 0 0 2 3h-16a4 4 0 0 0 2 -3v-3a7 7 0 0 1 4 -6" />
            <path d="M9 17v1a3 3 0 0 0 6 0v-1" />
          </svg>
          {unreadCount > 0 && (
            <span className="badge-notification" style={{ backgroundColor: COLOR_RED, color: getContrastColor(COLOR_RED) }}>{unreadCount > 99 ? '99+' : unreadCount}</span>
          )}
        </span>
      </a>

      {isOpen && (
        <div className="dropdown-menu dropdown-menu-arrow dropdown-menu-end dropdown-menu-card show">
          <div className="card">
            <div className="card-header" style={{ display: 'flex', alignItems: 'center' }}>
              <h3 className="card-title">{t('notifications.title')}</h3>
              {unreadCount > 0 && (
                <button
                  className="btn btn-link ms-auto"
                  onClick={() => markAllAsReadMutation.mutate()}
                >
                  {t('notifications.markAllRead')}
                </button>
              )}
            </div>
            <div className="list-group list-group-flush list-group-hoverable">
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border spinner-border-sm" role="status">
                    <span className="visually-hidden">{t('common.loading')}</span>
                  </div>
                </div>
              ) : notifications.length === 0 ? (
                <div className="list-group-item text-center text-secondary">
                  {t('notifications.noNotifications')}
                </div>
              ) : (
                notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`list-group-item ${!notification.is_read ? 'bg-light' : ''}`}
                    onClick={() => markAsReadMutation.mutate(notification.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="row align-items-center">
                      <div className="col-auto">
                        <span className={`status-dot ${!notification.is_read ? 'status-dot-animated bg-primary' : 'bg-secondary'} d-block`}></span>
                      </div>
                      <div className="col text-truncate">
                        <span className="text-body d-block">{notification.title}</span>
                        <div className="d-block text-muted text-truncate mt-n1">
                          {notification.message}
                        </div>
                        <div className="d-block text-muted text-truncate mt-1" style={{ fontSize: '0.75rem' }}>
                          {formatTime(notification.created_at)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            {notifications.length > 0 && (
              <div className="card-footer text-center">
                <Link to="/notifications" className="text-decoration-none" onClick={() => setIsOpen(false)}>
                  {t('notifications.viewAll')}
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
